import { Typewriter } from "react-simple-typewriter";
import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../css/HomePage.css";

import Marquee from "../components/Marquee";
import ReferencesCard from "../components/ReferencesCard";
import ContactForm from "../components/ContactForm";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import {
  Instagram, PenTool, Globe, Palette, Search, Brain, ArrowUpRight,
  Bot, Workflow, Sparkles, TrendingUp, Shield, Zap,
} from "lucide-react";

/* ───────────────────────── Styles ───────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@500;700;900&family=Manrope:wght@400;500;700&display=swap');

.x{
  --bg:oklch(0.14 0.025 270); --bg2:oklch(0.19 0.03 270); --fg:oklch(0.97 0.01 270); --mute:oklch(0.72 0.03 270);
  --a1:oklch(0.62 0.26 265); --a2:oklch(0.82 0.14 210); --a3:oklch(0.72 0.22 330);
  --line:oklch(1 0 0 / .1);
  font-family:'Manrope',system-ui,sans-serif; background:var(--bg); color:var(--fg); overflow-x:clip; position:relative;
}
.x *{ box-sizing:border-box; }
.x h1,.x h2,.x h3,.x .disp{ font-family:'Unbounded',sans-serif; font-weight:700; letter-spacing:-0.04em; line-height:1.02; }
.x .grad{ background:linear-gradient(100deg,var(--a2),var(--a1) 45%,var(--a3)); -webkit-background-clip:text; background-clip:text; color:transparent; }
.x::after{ content:""; position:fixed; inset:0; pointer-events:none; z-index:90; opacity:.06; mix-blend-mode:overlay;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }

/* hero */
.hero{ position:relative; min-height:100svh; padding:150px 24px 100px; display:flex; align-items:center; overflow:hidden; isolation:isolate; }
.aurora i{ position:absolute; border-radius:50%; filter:blur(90px); opacity:.55; z-index:-1; animation:drift 18s ease-in-out infinite alternate; }
.aurora i:nth-child(1){ width:620px; height:620px; background:var(--a1); top:-12%; left:-8%; }
.aurora i:nth-child(2){ width:520px; height:520px; background:var(--a3); right:-10%; top:20%; animation-duration:24s; opacity:.35; }
.aurora i:nth-child(3){ width:480px; height:480px; background:var(--a2); bottom:-18%; left:35%; animation-duration:21s; opacity:.3; }
@keyframes drift{ to{ transform:translate(80px,-60px) scale(1.2) rotate(30deg); } }
.hero::before{ content:""; position:absolute; inset:0; z-index:-1; pointer-events:none;
  background:radial-gradient(500px circle at var(--mx,50%) var(--my,40%), oklch(1 0 0 / .09), transparent 70%); }
.hero-grid{ max-width:1280px; margin:0 auto; width:100%; display:grid; grid-template-columns:1.25fr .75fr; gap:56px; align-items:center; }
.pill{ display:inline-flex; align-items:center; gap:10px; padding:9px 16px; border-radius:99px; border:1px solid var(--line);
  background:oklch(1 0 0 / .05); backdrop-filter:blur(12px); font-size:.9rem; color:var(--mute); }
.pill b{ width:8px; height:8px; border-radius:50%; background:var(--a2); box-shadow:0 0 0 0 var(--a2); animation:ping 2s infinite; }
@keyframes ping{ 70%{ box-shadow:0 0 0 10px transparent; } 100%{ box-shadow:0 0 0 0 transparent; } }
.hero h1{ font-size:clamp(2.4rem,6.4vw,5.6rem); margin:26px 0 0; }
.hero h1 .w{ display:inline-block; opacity:0; transform:translateY(40px) rotate(3deg); filter:blur(10px); animation:in .9s cubic-bezier(.16,1,.3,1) forwards; }
@keyframes in{ to{ opacity:1; transform:none; filter:none; } }
.hero .sub{ color:var(--mute); font-size:1.15rem; line-height:1.65; max-width:540px; margin:28px 0 36px; }
.cta{ display:flex; flex-wrap:wrap; gap:14px; }
.btn{ position:relative; display:inline-flex; align-items:center; gap:10px; padding:17px 28px; border-radius:99px; font:700 1rem 'Manrope',sans-serif;
  border:1px solid var(--line); background:oklch(1 0 0 / .05); color:var(--fg); cursor:pointer; backdrop-filter:blur(10px);
  transition:transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .3s, background .3s; will-change:transform; }
.btn:hover{ background:oklch(1 0 0 / .1); }
.btn.pri{ background:linear-gradient(110deg,var(--a1),var(--a3)); border-color:transparent; box-shadow:0 10px 40px -10px var(--a1); }
.btn.pri:hover{ box-shadow:0 16px 56px -8px var(--a3); }

/* glass live card */
.glass{ border:1px solid var(--line); background:linear-gradient(160deg,oklch(1 0 0 / .1),oklch(1 0 0 / .03)); backdrop-filter:blur(24px); border-radius:28px; }
.live{ padding:26px; transform:perspective(1000px) rotateY(calc(var(--rx,0) * -10deg)) rotateX(calc(var(--ry,0) * 8deg)); transition:transform .3s; }
.live-top{ display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; font-size:.85rem; color:var(--mute); }
.live-top span{ display:inline-flex; align-items:center; gap:8px; }
.live-top span::before{ content:""; width:8px; height:8px; border-radius:50%; background:#4ade80; animation:ping 2s infinite; box-shadow:0 0 0 0 #4ade80; }
.chart{ display:flex; align-items:flex-end; gap:8px; height:120px; margin:6px 0 22px; }
.chart i{ flex:1; border-radius:8px 8px 3px 3px; background:linear-gradient(to top,var(--a1),var(--a2)); height:var(--h); transform-origin:bottom; animation:grow 1.2s cubic-bezier(.16,1,.3,1) both; animation-delay:calc(var(--i) * .07s + .8s); }
@keyframes grow{ from{ transform:scaleY(0); } }
.kpis{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.kpis div{ padding:14px; border-radius:16px; background:oklch(1 0 0 / .05); }
.kpis small{ color:var(--mute); font-size:.75rem; } .kpis b{ display:block; font:700 1.5rem 'Unbounded'; margin-top:4px; letter-spacing:-0.04em; }

/* marquee */
.strip{ border-block:1px solid var(--line); overflow:hidden; white-space:nowrap; padding:26px 0; }
.strip .t{ display:inline-flex; gap:3rem; animation:slide 30s linear infinite; font:900 clamp(2.4rem,6vw,4.6rem)/1 'Unbounded'; letter-spacing:-0.04em; }
.strip span{ color:transparent; -webkit-text-stroke:1.5px oklch(1 0 0 / .45); transition:color .3s; } .strip span:nth-child(3n){ color:var(--fg); -webkit-text-stroke:0; }
@keyframes slide{ to{ transform:translateX(-50%); } }

/* sections */
.sec{ max-width:1280px; margin:0 auto; padding:140px 24px; }
.sec h2{ font-size:clamp(2rem,5vw,4.2rem); }
.statement{ font:700 clamp(1.7rem,4vw,3.4rem)/1.2 'Unbounded'; letter-spacing:-0.035em; max-width:1100px; }
.statement span{ color:oklch(1 0 0 / .18); transition:color .35s; } .statement span.on{ color:var(--fg); }

/* bento */
.bento{ display:grid; grid-template-columns:repeat(6,1fr); gap:16px; margin-top:64px; }
.cell{ position:relative; padding:30px; min-height:260px; display:flex; flex-direction:column; justify-content:space-between; overflow:hidden;
  transition:transform .5s cubic-bezier(.16,1,.3,1); }
.cell:nth-child(1){ grid-column:span 3; } .cell:nth-child(2){ grid-column:span 3; } .cell:nth-child(3){ grid-column:span 2; }
.cell:nth-child(4){ grid-column:span 2; } .cell:nth-child(5){ grid-column:span 2; } .cell:nth-child(6){ grid-column:span 6; min-height:200px; }
.cell::before{ content:""; position:absolute; inset:0; pointer-events:none; opacity:0; transition:opacity .3s;
  background:radial-gradient(380px circle at var(--x) var(--y), oklch(0.62 0.26 265 / .28), transparent 65%); }
.cell:hover{ transform:translateY(-6px); } .cell:hover::before{ opacity:1; }
.cell .ic{ width:54px; height:54px; border-radius:16px; display:grid; place-items:center; background:oklch(1 0 0 / .08); border:1px solid var(--line); transition:transform .5s, background .3s; }
.cell:hover .ic{ transform:rotate(-10deg) scale(1.12); background:linear-gradient(135deg,var(--a1),var(--a3)); }
.cell h3{ font-size:1.5rem; margin:36px 0 10px; } .cell p{ color:var(--mute); line-height:1.6; font-size:.98rem; max-width:460px; }
.cell .arr{ position:absolute; top:26px; right:26px; opacity:.35; transition:.4s; } .cell:hover .arr{ opacity:1; transform:translate(4px,-4px); }

/* stacked cards */
.stack{ display:grid; gap:28px; margin-top:64px; }
.sc{ position:sticky; top:calc(96px + var(--n) * 22px); min-height:430px; padding:clamp(26px,4vw,52px); border-radius:36px; border:1px solid var(--line);
  display:grid; grid-template-columns:1fr 1fr; gap:36px; overflow:hidden; box-shadow:0 -30px 60px -30px #000; }
.sc:nth-child(1){ background:linear-gradient(135deg,oklch(0.3 0.15 265),var(--bg2)); }
.sc:nth-child(2){ background:linear-gradient(135deg,oklch(0.3 0.14 330),var(--bg2)); }
.sc:nth-child(3){ background:linear-gradient(135deg,oklch(0.3 0.09 210),var(--bg2)); }
.sc h3{ font-size:clamp(1.6rem,3vw,2.6rem); margin:20px 0 16px; } .sc p{ color:var(--mute); line-height:1.65; }
.tag{ display:inline-block; padding:8px 14px; border-radius:99px; border:1px solid var(--line); background:oklch(1 0 0 / .06); font-size:.85rem; margin:0 8px 8px 0; }
.bar{ height:10px; border-radius:99px; background:oklch(1 0 0 / .1); overflow:hidden; margin:8px 0 22px; }
.bar i{ display:block; height:100%; width:0; border-radius:99px; background:linear-gradient(90deg,var(--a2),var(--a1)); transition:width 1.4s cubic-bezier(.16,1,.3,1) .2s; }
.minis{ display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.minis div{ padding:20px; border-radius:20px; background:oklch(1 0 0 / .06); border:1px solid var(--line); }
.minis h4{ font:700 1rem 'Unbounded'; letter-spacing:-0.03em; margin:14px 0 6px; } .minis p{ font-size:.88rem; }

/* stats */
.stats{ display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); }
.stats > div{ padding:44px 30px; border-right:1px solid var(--line); } .stats > div:last-child{ border:0; }
.stats b{ display:block; font:900 clamp(2.8rem,5.5vw,4.6rem)/1 'Unbounded'; letter-spacing:-0.05em; }
.stats span{ color:var(--mute); }

/* contact + modal */
.contact{ padding:60px 24px 140px; }
.contact-box{ max-width:960px; margin:0 auto; padding:clamp(28px,6vw,72px); position:relative; }
.contact-box::before{ content:""; position:absolute; inset:-1px; border-radius:inherit; padding:1px; pointer-events:none;
  background:conic-gradient(from var(--ang,0deg),transparent 60%,var(--a2),var(--a1),var(--a3),transparent); animation:spinb 6s linear infinite;
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude; }
@property --ang{ syntax:'<angle>'; inherits:false; initial-value:0deg; }
@keyframes spinb{ to{ --ang:360deg; } }
.modal{ position:fixed; inset:0; z-index:300; display:grid; place-items:center; padding:20px; }
.modal-bg{ position:absolute; inset:0; background:oklch(0.1 0.02 270 / .7); backdrop-filter:blur(14px); animation:in .3s both; }
.modal-card{ position:relative; width:100%; max-width:500px; padding:42px; background:oklch(0.2 0.03 270 / .85); animation:pop .5s cubic-bezier(.16,1.1,.3,1) both; }
@keyframes pop{ from{ opacity:0; transform:translateY(30px) scale(.94); } }
.modal input{ width:100%; padding:17px 20px; border-radius:16px; border:1px solid var(--line); background:oklch(1 0 0 / .06); color:var(--fg); font:inherit; font-size:1.05rem; }
.modal input:focus{ outline:2px solid var(--a1); outline-offset:2px; }

.rv{ opacity:0; transform:translateY(36px); filter:blur(6px); transition:opacity .9s, transform .9s cubic-bezier(.16,1,.3,1), filter .9s; }
.rv.on{ opacity:1; transform:none; filter:none; }
.x :focus-visible{ outline:2px solid var(--a2); outline-offset:3px; }

@media(max-width:960px){
  .hero-grid{ grid-template-columns:1fr; } .live{ display:none; }
  .bento .cell{ grid-column:span 6 !important; } .sc{ grid-template-columns:1fr; position:relative; top:0; }
}
@media (prefers-reduced-motion:reduce){
  .x *,.x *::before{ animation-duration:.01ms !important; animation-iteration-count:1 !important; transition-duration:.01ms !important; }
  .hero h1 .w{ opacity:1; transform:none; filter:none; } .rv{ opacity:1; transform:none; filter:none; }
}
`;

/* ───────────────────────── Hooks & helpers ───────────────────────── */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold });
    io.observe(el); return () => io.disconnect();
  }, [threshold]);
  return [ref, on];
}

function Rv({ children, className = "", delay = 0, style, ...p }) {
  const [ref, on] = useReveal();
  return <div ref={ref} className={`rv ${on ? "on" : ""} ${className}`} style={{ transitionDelay: `${delay}s`, ...style }} {...p}>{children}</div>;
}

function CountUp({ to, suffix = "", decimals = 0, run }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf, s;
    const tick = (ts) => { s ??= ts; const p = Math.min((ts - s) / 1800, 1); setV((1 - Math.pow(1 - p, 4)) * to); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf);
  }, [run, to]);
  return <>{v.toFixed(decimals)}{suffix}</>;
}

/* kelime kelime yanan metin (scroll'a bağlı) */
function ScrollText({ text }) {
  const ref = useRef(null);
  const [p, setP] = useState(0);
  const words = useMemo(() => text.split(" "), [text]);
  useEffect(() => {
    const f = () => {
      const r = ref.current?.getBoundingClientRect(); if (!r) return;
      const vh = window.innerHeight;
      setP(Math.min(Math.max((vh * 0.85 - r.top) / (r.height + vh * 0.35), 0), 1));
    };
    f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <p className="statement" ref={ref}>
      {words.map((w, i) => <span key={i} className={i / words.length < p ? "on" : ""}>{w} </span>)}
    </p>
  );
}

function Magnetic({ children, ...p }) {
  const move = (e) => {
    const b = e.currentTarget, r = b.getBoundingClientRect();
    b.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.25}px, ${(e.clientY - r.top - r.height / 2) * 0.35}px)`;
  };
  return <button {...p} onMouseMove={move} onMouseLeave={(e) => (e.currentTarget.style.transform = "")}>{children}</button>;
}

const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", e.clientX - r.left + "px");
  e.currentTarget.style.setProperty("--y", e.clientY - r.top + "px");
};

/* ───────────────────────── Page ───────────────────────── */
export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [popUp, setPopUp] = useState(false);

  const heroRef = useRef(null);
  const [aiRef, aiOn] = useReveal(0.2);
  const [statsRef, statsOn] = useReveal(0.4);

  const onHeroMove = (e) => {
    const r = heroRef.current.getBoundingClientRect(), s = heroRef.current.style;
    s.setProperty("--mx", e.clientX - r.left + "px");
    s.setProperty("--my", e.clientY - r.top + "px");
    s.setProperty("--rx", ((e.clientX - r.left) / r.width - 0.5).toFixed(2));
    s.setProperty("--ry", ((e.clientY - r.top) / r.height - 0.5).toFixed(2));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(false); setSuccess(false);
    const formData = new FormData(e.target);
    try {
      const res = await fetch("https://formsubmit.co/ajax/duruoguz5451@gmail.com", { method: "POST", body: formData, headers: { Accept: "application/json" } });
      if (res.ok) { setSuccess(true); e.target.reset(); } else setError(true);
    } catch { setError(true); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
        setTimeout(() => { window.scrollTo({ top: y, behavior: "smooth" }); navigate(".", { replace: true, state: null }); }, 100);
      }
    }
  }, [location, navigate]);

  const goReferences = () => navigate("/references");

  const services = [
    { icon: Instagram, k: "service_instagram" }, { icon: PenTool, k: "service_logo" }, { icon: Globe, k: "service_web" },
    { icon: Palette, k: "service_grafik" }, { icon: Search, k: "service_seo" }, { icon: Brain, k: "service_brand" },
  ];
  const minis = [
    { icon: Sparkles, h: "AI İçerik", p: "Hedef kitlene özel içerik, saniyeler içinde." },
    { icon: TrendingUp, h: "Büyüme Analitiği", p: "Gerçek zamanlı veri ve akıllı öneriler." },
    { icon: Shield, h: "Marka Tutarlılığı", p: "Her platformda tek ses, tek kimlik." },
    { icon: Zap, h: "Hızlı Teslimat", p: "Projeleri zamanında teslim ediyoruz." },
  ];
  const words = ["Sosyal Medya", "Logo", "Web", "Grafik", "SEO", "Marka", "Yapay Zeka", "Otomasyon"];
  const title = t("hero_title").split(" ");

  return (
    <div className="x">
      <style>{css}</style>
      <Navbar />

      {/* HERO */}
      <section id="home" className="hero" ref={heroRef} onMouseMove={onHeroMove}>
        <div className="aurora" aria-hidden="true"><i /><i /><i /></div>
        <div className="hero-grid">
          <div>
            <div className="pill"><b />{t("section_best")}</div>
            <h1>
              {title.map((w, i) => <span key={i} className="w" style={{ animationDelay: `${i * 0.08}s` }}>{w}&nbsp;</span>)}
              <br />
              <span className="grad">
                <Typewriter words={[t("hero_typewriter1"), t("hero_typewriter2"), t("hero_typewriter3")]}
                  loop cursor cursorStyle="_" typeSpeed={70} deleteSpeed={50} delaySpeed={2000} />
              </span>
            </h1>
            <p className="sub">{t("hero_desc")}</p>
            <div className="cta">
              <Magnetic className="btn pri" onClick={() => setPopUp(true)}>{t("hero_btn")} <ArrowUpRight size={18} /></Magnetic>
              <Magnetic className="btn" onClick={goReferences}>{t("section_btn_jobs")}</Magnetic>
            </div>
          </div>

          <div className="glass live" aria-hidden="true">
            <div className="live-top"><span>AI Agent aktif</span><span style={{ display: "block" }}>son 30 gün</span></div>
            <div className="chart">
              {[28, 40, 34, 52, 46, 64, 58, 78, 70, 92].map((h, i) => <i key={i} style={{ "--h": h + "%", "--i": i }} />)}
            </div>
            <div className="kpis">
              {[["Erişim", "+340%"], ["Dönüşüm", "+128%"], ["Etkileşim", "+89%"], ["ROI", "4.2x"]].map(([l, v]) => (
                <div key={l}><small>{l}</small><b className="grad">{v}</b></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="strip" aria-hidden="true">
        <div className="t">
          {[0, 1].map((k) => <span key={k} style={{ display: "inline-flex", gap: "3rem" }}>{[...words, ...words].map((w, i) => <span key={i}>{w}</span>)}</span>)}
        </div>
      </div>

      {/* STATEMENT + SERVICES */}
      <section id="home-about" className="sec">
        <ScrollText text={`${t("section_markani_buyut")}. ${t("section_desc")}`} />
        <div className="bento">
          {services.map(({ icon: Icon, k }, i) => (
            <Rv key={k} className="glass cell" delay={(i % 3) * 0.08} onMouseMove={spot}>
              <ArrowUpRight className="arr" size={22} />
              <div className="ic"><Icon size={24} /></div>
              <div><h3>{t(k)}</h3><p>{t(k + "_desc")}</p></div>
            </Rv>
          ))}
        </div>
      </section>

      {/* AI — STACKED CARDS */}
      <section className="sec" style={{ paddingTop: 20 }} ref={aiRef}>
        <Rv>
          <h2>Yapay Zeka ile <span className="grad">Markanı Geleceğe Taşı</span></h2>
          <p style={{ color: "var(--mute)", maxWidth: 560, lineHeight: 1.65, marginTop: 22 }}>{t("ai_content")}</p>
        </Rv>
        <div className="stack">
          <div className="sc" style={{ "--n": 0 }}>
            <div>
              <Bot size={34} />
              <h3>{t("card_1")}</h3><p>{t("card_1_p")}</p>
            </div>
            <div style={{ alignSelf: "end" }}>
              {["GPT-4o", "Claude AI", "Otomasyon", "Analitik"].map((x) => <span className="tag" key={x}>{x}</span>)}
            </div>
          </div>
          <div className="sc" style={{ "--n": 1 }}>
            <div><Workflow size={34} /><h3>{t("card_2")}</h3><p>{t("card_2_p")}</p></div>
            <div style={{ alignSelf: "center" }}>
              {[["Veri Toplama", 100], ["AI Analiz", 85], ["Otomatik Yayın", 70]].map(([l, p]) => (
                <div key={l}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}><span>{l}</span><span>{p}%</span></div>
                  <div className="bar"><i style={{ width: aiOn ? p + "%" : 0 }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="sc" style={{ "--n": 2 }}>
            <div><Sparkles size={34} /><h3>Hepsi tek ekipte</h3><p>İçerikten analitiğe, tasarımdan yayına; AI destekli süreçle hızlı ve tutarlı çalışıyoruz.</p></div>
            <div className="minis">
              {minis.map(({ icon: Icon, h, p }) => <div key={h}><Icon size={20} /><h4>{h}</h4><p>{p}</p></div>)}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="sec" style={{ paddingBlock: 40 }}>
        <div className="glass stats" ref={statsRef}>
          <div><b className="grad"><CountUp to={50} suffix="+" run={statsOn} /></b><span>Tamamlanan proje</span></div>
          <div><b className="grad"><CountUp to={50} suffix="+" run={statsOn} /></b><span>Mutlu marka</span></div>
          <div><b className="grad"><CountUp to={4.2} decimals={1} suffix="x" run={statsOn} /></b><span>Ortalama ROI</span></div>
          <div><b className="grad">24/7</b><span>AI çalışma süresi</span></div>
        </div>
      </section>

      <div style={{ padding: "100px 0 20px", opacity: 0.5 }}><Marquee /></div>

      {/* REFERENCES */}
      <section id="home-references" className="sec" style={{ textAlign: "center" }}>
        <Rv><h2>{t("references_title")}</h2></Rv>
        <Rv className="glass" style={{ padding: "clamp(18px,4vw,44px)", marginTop: 56 }}><ReferencesCard limit={3} /></Rv>
        <div style={{ marginTop: 44 }}>
          <Magnetic className="btn pri" onClick={goReferences}>{t("references_more")} <ArrowUpRight size={18} /></Magnetic>
        </div>
      </section>

      {/* CONTACT */}
      <section id="home-contact" className="contact">
        <Rv className="glass contact-box"><ContactForm /></Rv>
      </section>

      <Footer />

      {/* MODAL */}
      {popUp && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal-bg" onClick={() => !loading && setPopUp(false)} />
          <div className="glass modal-card">
            <button onClick={() => setPopUp(false)} aria-label="Kapat" style={{ position: "absolute", top: 18, right: 22, background: "none", border: 0, color: "var(--mute)", fontSize: 22, cursor: "pointer" }}>✕</button>
            {success ? (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg,var(--a1),var(--a3))", display: "grid", placeItems: "center", fontSize: 36, margin: "0 auto 20px" }}>✓</div>
                <h3 style={{ fontSize: "1.8rem", marginBottom: 10 }}>Gönderildi</h3>
                <p style={{ color: "var(--mute)" }}>Mesajın bize ulaştı, en kısa sürede dönüş yapacağız.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 style={{ fontSize: "1.9rem", marginBottom: 12 }}>{t("popup_title")}</h3>
                <p style={{ color: "var(--mute)", marginBottom: 26, lineHeight: 1.55 }}>{t("popup_desc")}</p>
                <label htmlFor="ig" style={{ fontWeight: 700, display: "block", marginBottom: 8 }}>{t("popup_label_instagram")}</label>
                <input id="ig" type="text" name="instagram" required disabled={loading} placeholder="@markanız" />
                {error && <p style={{ color: "#fb7185", fontWeight: 600, marginTop: 14 }}>Gönderilemedi. Bağlantını kontrol edip tekrar dene.</p>}
                <button type="submit" disabled={loading} className="btn pri" style={{ width: "100%", justifyContent: "center", marginTop: 24, opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Gönderiliyor…" : t("popup_submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}